/**
 * Bid Validation Utilities
 *
 * Pure functions for bridge bidding rules validation.
 * Source: specs/004-legal-bid-validation/research.md
 */

import type {
  ParsedBid,
  HighestBid,
  AuctionState,
  ContractState,
  Side,
  Position,
  LegalBids,
} from '../types/problems';
import { parseBidWithComment } from './bidParser';

// =============================================================================
// Constants
// =============================================================================

/**
 * Numeric index for suit ranking comparison.
 * Clubs (lowest) < Diamonds < Hearts < Spades < No Trump (highest)
 */
export const SUIT_ORDER: Record<string, number> = {
  'C': 0,   // Clubs (lowest)
  'D': 1,   // Diamonds
  'H': 2,   // Hearts
  'S': 3,   // Spades
  'NT': 4,  // No Trump (highest)
};

/**
 * Bidding order starting from dealer (West is always first in "The Auction" display).
 */
export const POSITION_ORDER: readonly Position[] = ['W', 'N', 'E', 'S'];

/**
 * Recognized bid format patterns for parsing.
 */
export const BID_FORMATS = {
  /** Regex for suit/NT bids like "1C", "2NT", "7S". The `NT?` token accepts both
   *  the canonical `NT` form and the single-char `N` shorthand used by source data
   *  out of `cardsgamehands.biddingsequence.bid` (and occasionally by older rows in
   *  `problems.json_data->'auction'`). Both forms are normalised to canonical `NT`
   *  before lookup against `SUIT_ORDER` — see `parseBid` below. */
  suitBid: /^(\d)([CDHS]|NT?)$/i,

  /** Symbol mappings for graphical suit characters */
  symbols: {
    '\u2663': 'C',  // ♣
    '\u2666': 'D',  // ♦
    '\u2665': 'H',  // ♥
    '\u2660': 'S',  // ♠
  } as Record<string, string>,

  /** Pass variants (case-insensitive) */
  pass: ['pass', 'p', '-'],

  /** Double variants (case-insensitive) */
  double: ['x', 'dbl', 'double'],

  /** Redouble variants (case-insensitive) */
  redouble: ['xx', 'rdbl', 'redouble'],

  /** Placeholder for user's turn (treated as end of auction for state computation) */
  placeholder: ['?'],
};

/**
 * All valid suits in display order.
 */
export const ALL_SUITS = ['C', 'D', 'H', 'S', 'NT'];

/**
 * All valid levels.
 */
export const ALL_LEVELS = [1, 2, 3, 4, 5, 6, 7];

// =============================================================================
// Bid Parsing Functions
// =============================================================================

/**
 * Parse a single bid string into a normalized ParsedBid object.
 * Handles various formats: "1C", "1♣", "Pass", "P", "X", "XX", "Dbl", "Rdbl"
 *
 * Unrecognised or malformed tokens do NOT throw — they return `{ type: 'unknown' }`,
 * a non-affecting call (handled like a placeholder in `computeAuctionState`). This keeps
 * a single bad call in the auction data (e.g. a stray LIN control tag such as "st" that
 * leaked in during hand import) from throwing and blank-screening the whole auction
 * display via React's error boundary. The spec treats auction-sequence legality as out of
 * scope (FR validates on the highest bid present), so tolerating junk tokens is correct.
 *
 * @param raw - The raw bid string from auction data
 * @returns ParsedBid - Normalized bid representation, or `{ type: 'unknown' }` when the
 *                       token is empty/non-string/unrecognised
 */
export function parseBid(raw: string): ParsedBid {
  if (!raw || typeof raw !== 'string') {
    return { type: 'unknown' };
  }

  // Use existing parseBidWithComment utility to strip annotations
  // This handles both parenthetical ("1NT (15-17)") and space-separated ("1d precision") annotations
  const { bid: cleanBid } = parseBidWithComment(raw);

  // Generic suit letters — a level plus a CLASS of suit rather than a suit — are read as the
  // HIGHEST suit the letter can stand for, so that nothing computed from the result (the
  // standing bid, the legal bids offered next) can be wrong under any suit the author might
  // have meant (feature 029, FR-044). The meanings are the client's, confirmed 2026-09-11:
  //   M  a major (H or S)      → S      m  a minor (C or D)                 → D
  //   x  any suit              → S      y  any suit ranking above x         → S
  // Resolved BEFORE upper-casing, because the case of the letter IS the meaning: "2m" is a
  // minor and "2M" a major, and an upper-cased "2M" could not tell them apart. Only a levelled
  // letter is touched — a bare "X" is still a double, further down.
  //
  // Until 2026-09-11 this read "x" as a MINOR (→ D) and any-case "m" as a MAJOR (→ S), and did
  // not read "y" at all. Both parsers of this text — this one and the C# port in
  // Libraries/BiddingLegality/BidToken.cs — moved together, and the shared corpus at
  // InHouseApps/BridgeDataValidation/tests/corpus/bid-token-corpus.json pins the new reading.
  const resolved = cleanBid
    .trim()
    .replace(/^([1-7])M$/, '$1S')
    .replace(/^([1-7])m$/, '$1D')
    .replace(/^([1-7])[xXyY]$/, '$1S');
  let processed = resolved.toUpperCase();

  // Replace suit symbols with letters
  for (const [symbol, letter] of Object.entries(BID_FORMATS.symbols)) {
    processed = processed.replace(symbol, letter);
  }

  // Slash notation like "2H/2S" → a generic major, mapped to its highest suit.
  const slashMatch = processed.match(/^(\d)[CDHS]\/\d[CDHS]$/i);
  if (slashMatch && slashMatch[1]) {
    const level = parseInt(slashMatch[1], 10);
    if (level >= 1 && level <= 7) {
      return { type: 'bid', level, suit: 'S' };
    }
  }

  // Try to match suit/NT bid pattern (e.g., "1C", "2NT", "7S", "1N")
  const match = processed.match(BID_FORMATS.suitBid);
  if (match && match[1] && match[2]) {
    const level = parseInt(match[1], 10);
    // Normalise the single-char `N` shorthand (from cardsgamehands.biddingsequence
    // and occasional problems.json_data rows) to canonical `NT` before SUIT_ORDER lookup.
    const rawSuit = match[2].toUpperCase();
    const suit = rawSuit === 'N' ? 'NT' : rawSuit;
    if (level >= 1 && level <= 7 && suit in SUIT_ORDER) {
      return { type: 'bid', level, suit };
    }
  }

  // Check special calls (case-insensitive) - use processed string with annotations stripped
  const normalized = processed.toLowerCase();

  if (BID_FORMATS.pass.includes(normalized)) {
    return { type: 'pass' };
  }

  // Check redouble BEFORE double (since "xx" should match redouble, not double twice)
  if (BID_FORMATS.redouble.includes(normalized)) {
    return { type: 'redouble' };
  }

  if (BID_FORMATS.double.includes(normalized)) {
    return { type: 'double' };
  }

  // Check for placeholder (user's turn marker)
  if (BID_FORMATS.placeholder.includes(normalized)) {
    return { type: 'placeholder' };
  }

  // Unrecognised token — degrade gracefully instead of throwing. Warn so malformed
  // source data stays discoverable (e.g. a stray LIN control tag that leaked into a
  // downloaded auction) without taking down the entire auction display.
  console.warn(`parseBid: unrecognised bid token "${raw}" — treating as non-affecting call.`);
  return { type: 'unknown' };
}

/**
 * Parse an array of bid strings into ParsedBid objects.
 *
 * @param bids - Array of raw bid strings from auction data
 * @returns ParsedBid[] - Array of normalized bids
 */
export function parseBids(bids: string[]): ParsedBid[] {
  if (!Array.isArray(bids)) {
    return [];
  }
  return bids.map(parseBid);
}

// =============================================================================
// Bid Comparison Functions
// =============================================================================

/**
 * Calculate the numeric value of a bid for comparison.
 * Value = (level * 5) + suit index
 * Range: 5 (1C) to 39 (7NT)
 *
 * @param level - Bid level (1-7)
 * @param suit - Suit ('C', 'D', 'H', 'S', 'NT')
 * @returns number - Bid value for comparison
 */
export function getBidValue(level: number, suit: string): number {
  const suitIndex = SUIT_ORDER[suit];
  if (suitIndex === undefined) {
    throw new Error(`Invalid suit: ${suit}`);
  }
  return (level * 5) + suitIndex;
}

/**
 * Check if a proposed bid is higher than the current highest bid.
 *
 * @param newLevel - Proposed bid level
 * @param newSuit - Proposed bid suit
 * @param highestBid - Current highest bid (or null if opening)
 * @returns boolean - True if the proposed bid is legal
 */
export function isLegalBid(
  newLevel: number,
  newSuit: string,
  highestBid: HighestBid | null
): boolean {
  if (!highestBid) {
    // Opening bid - any suit/level combination is legal
    return true;
  }
  return getBidValue(newLevel, newSuit) > highestBid.value;
}

// =============================================================================
// Position and Side Functions
// =============================================================================

/**
 * Get the partnership side for a given position.
 *
 * @param position - Player position ('N', 'E', 'S', 'W')
 * @returns Side - Partnership ('NS' or 'EW')
 */
export function getSide(position: Position): Side {
  return (position === 'N' || position === 'S') ? 'NS' : 'EW';
}

/**
 * Get the current bidder position based on auction length.
 * Bidding order: W -> N -> E -> S -> W -> ...
 *
 * @param auctionLength - Number of calls made so far
 * @returns Position - Current bidder
 */
export function getCurrentBidder(auctionLength: number): Position {
  const position = POSITION_ORDER[auctionLength % 4];
  // TypeScript safety: modulo 4 always yields 0-3, which are valid indices
  return position as Position;
}

/**
 * Get the position that made a bid at a given index in the auction.
 *
 * @param bidIndex - Index of the bid in the auction array
 * @returns Position - Position that made the bid
 */
export function getPositionAtIndex(bidIndex: number): Position {
  const position = POSITION_ORDER[bidIndex % 4];
  // TypeScript safety: modulo 4 always yields 0-3, which are valid indices
  return position as Position;
}

// =============================================================================
// Auction State Computation
// =============================================================================

/**
 * Compute the complete auction state from an array of parsed bids.
 * This determines the highest bid, contract state, and who can double/redouble.
 *
 * @param parsedBids - Array of parsed bids
 * @param userPosition - The position of the user making the next bid
 * @param bidderPositions - Optional array of bidder positions for each bid (when available from auction data)
 * @returns AuctionState - Complete state needed for validation
 */
export function computeAuctionState(
  parsedBids: ParsedBid[],
  userPosition: Position,
  bidderPositions?: Position[]
): AuctionState {
  let highestBid: HighestBid | null = null;
  let contractState: ContractState = 'none';
  let lastBidSide: Side | null = null;

  for (let i = 0; i < parsedBids.length; i++) {
    const bid = parsedBids[i];
    if (!bid) continue; // TypeScript safety: should never happen with valid array
    // Use provided bidder position if available, otherwise fall back to index-based calculation
    const bidderPosition = bidderPositions?.[i] ?? getPositionAtIndex(i);
    const bidderSide = getSide(bidderPosition);

    switch (bid.type) {
      case 'bid': {
        const { level, suit } = bid;
        if (level !== undefined && suit !== undefined) {
          highestBid = {
            level,
            suit,
            value: getBidValue(level, suit),
          };
          contractState = 'undoubled';
          lastBidSide = bidderSide;
        }
        break;
      }

      case 'double':
        if (contractState === 'undoubled') {
          contractState = 'doubled';
        }
        break;

      case 'redouble':
        if (contractState === 'doubled') {
          contractState = 'redoubled';
        }
        break;

      case 'pass':
        // Pass doesn't change the auction state
        break;

      case 'placeholder':
        // Placeholder (?) marks user's turn - doesn't change auction state
        break;

      case 'unknown':
        // Unrecognised/malformed token (e.g. junk leaked into auction data) -
        // treated as a non-affecting call so it can't corrupt or crash the auction state
        break;
    }
  }

  return {
    highestBid,
    contractState,
    lastBidSide,
    currentBidder: userPosition,
    callCount: parsedBids.length,
  };
}

// =============================================================================
// Double/Redouble Validation
// =============================================================================

/**
 * Check if Double is legal for the current bidder.
 * Double is legal when:
 * - There is an undoubled contract (a suit/NT bid has been made, not yet doubled)
 * - The current bidder is on the opposing side from who made the last suit bid
 *
 * @param state - Current auction state
 * @returns boolean - True if Double is legal
 */
export function canDouble(state: AuctionState): boolean {
  // Can only double if there's an undoubled contract
  if (state.contractState !== 'undoubled') {
    return false;
  }

  // Can only double opponent's bid
  if (!state.lastBidSide) {
    return false;
  }

  const currentSide = getSide(state.currentBidder);
  return currentSide !== state.lastBidSide;
}

/**
 * Check if Redouble is legal for the current bidder.
 * Redouble is legal when:
 * - The contract has been doubled
 * - The current bidder is on the same side that was doubled (the side that made the last suit bid)
 *
 * @param state - Current auction state
 * @returns boolean - True if Redouble is legal
 */
export function canRedouble(state: AuctionState): boolean {
  // Can only redouble if contract is doubled
  if (state.contractState !== 'doubled') {
    return false;
  }

  // Can only redouble if we're on the side that was doubled
  if (!state.lastBidSide) {
    return false;
  }

  const currentSide = getSide(state.currentBidder);
  return currentSide === state.lastBidSide;
}

// =============================================================================
// Legal Level and Suit Computation
// =============================================================================

/**
 * Get all levels that have at least one legal suit.
 * A level is legal if at least one suit at that level beats the current highest bid.
 *
 * @param highestBid - Current highest bid (or null if opening)
 * @returns number[] - Array of legal levels (1-7)
 */
export function getLegalLevels(highestBid: HighestBid | null): number[] {
  if (!highestBid) {
    // Opening bid - all levels are legal
    return [...ALL_LEVELS];
  }

  const legalLevels: number[] = [];

  for (const level of ALL_LEVELS) {
    if (level > highestBid.level) {
      // Higher level - all suits are legal
      legalLevels.push(level);
    } else if (level === highestBid.level) {
      // Same level - check if any higher suit exists
      const currentSuitIndex = SUIT_ORDER[highestBid.suit] ?? 0;
      // If current suit is not NT (index 4), there are higher suits available
      if (currentSuitIndex < 4) {
        legalLevels.push(level);
      }
    }
    // Lower levels have no legal suits
  }

  return legalLevels;
}

/**
 * Get legal suits for a specific level given the current highest bid.
 *
 * @param level - The level to check
 * @param highestBid - Current highest bid (or null if opening)
 * @returns string[] - Array of legal suits for this level
 */
export function getLegalSuitsForLevel(
  level: number,
  highestBid: HighestBid | null
): string[] {
  if (!highestBid) {
    // Opening bid - all suits are legal at any level
    return [...ALL_SUITS];
  }

  if (level > highestBid.level) {
    // Higher level - all suits are legal
    return [...ALL_SUITS];
  }

  if (level === highestBid.level) {
    // Same level - only suits higher than current bid's suit
    const currentSuitIndex = SUIT_ORDER[highestBid.suit] ?? 0;
    return ALL_SUITS.filter((_, idx) => idx > currentSuitIndex);
  }

  // Lower level - no legal suits
  return [];
}

// =============================================================================
// Combined Legal Bids Computation
// =============================================================================

/**
 * Compute all legal bids given the auction state and optional selected level.
 * This is the main function used by the useLegalBids hook.
 *
 * @param state - Current auction state
 * @param selectedLevel - Currently selected level (optional, for dynamic suit filtering)
 * @returns LegalBids - All available legal bids
 */
export function computeLegalBids(
  state: AuctionState,
  selectedLevel?: number | null
): LegalBids {
  const levels = getLegalLevels(state.highestBid);

  // Determine which suits to show based on selected level
  let suits: string[];
  if (selectedLevel !== null && selectedLevel !== undefined && levels.includes(selectedLevel)) {
    suits = getLegalSuitsForLevel(selectedLevel, state.highestBid);
  } else if (levels.length > 0 && levels[0] !== undefined) {
    // No level selected or invalid level - show suits for the first legal level
    suits = getLegalSuitsForLevel(levels[0], state.highestBid);
  } else {
    // No legal levels (e.g., after 7NT)
    suits = [];
  }

  return {
    levels,
    suits,
    canPass: true, // Pass is always legal
    canDouble: canDouble(state),
    canRedouble: canRedouble(state),
  };
}
